import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Interim jump-page (support-admin Phase 2, 869f0ev2j/869f0ev2r): the real
 * user/org search screen is a SIBLING task's build (support-admin-proposal.md
 * §5, checklist item 1a) and is not merged yet. Until it lands, the "Users"
 * nav item points here — paste an organization id, jump straight to its
 * credits or plan screen. Replace this page's body with a real search result
 * list once the sibling's search endpoint exists; the two destination routes
 * (`/organizations/:id/credits`, `/organizations/:id/plan`) do not change.
 */
export function OrganizationLookupPage() {
  const [orgId, setOrgId] = useState('');
  const navigate = useNavigate();

  function goTo(path: 'credits' | 'plan') {
    return (e: FormEvent) => {
      e.preventDefault();
      const id = orgId.trim();
      if (!id) return;
      navigate(`/organizations/${id}/${path}`);
    };
  }

  return (
    <div className="max-w-md">
      <h1 className="text-h2 font-semibold text-fg-primary">Organizations</h1>
      <p className="mt-2 text-body text-fg-secondary">
        Paste an organization id to open its credit ledger or plan. (User/organization search
        lands separately — this is a jump-page until then.)
      </p>
      <form className="mt-6 flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-meta font-medium text-fg-secondary">
          Organization id
          <input
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
            placeholder="00000000-0000-0000-0000-000000000000"
            className="rounded-card border border-border bg-surface-field px-3 py-2 text-body text-fg-primary"
          />
        </label>
        <div className="flex gap-2">
          <button
            type="submit"
            onClick={goTo('credits')}
            disabled={!orgId.trim()}
            className="rounded-card bg-primary px-4 py-2 text-body font-medium text-primary-foreground hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Open credits
          </button>
          <button
            type="submit"
            onClick={goTo('plan')}
            disabled={!orgId.trim()}
            className="rounded-card border border-border px-4 py-2 text-body font-medium text-fg-primary hover:bg-state-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            Open plan
          </button>
        </div>
      </form>
    </div>
  );
}
